import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { DetailShell, InfoRow, InfoGrid } from "@/components/DetailShell";
import { StatusBadge, Empty } from "@/components/primitives";
import { toast } from "sonner";

export default function MachineDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/machines/${id}`);
        setData(data);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="phizo-label">Loading machine…</div>;
  if (!data || !data.machine) return <Empty label="Machine not found." />;
  const m = data.machine;
  const logs = data.maintenance || [];

  return (
    <DetailShell
      overline="FLEET / MACHINE"
      title={m.name}
      subtitle={`${m.asset_tag} · ${m.category || "—"}`}
      backTo="/machines"
      testId="machine-detail"
      actions={<StatusBadge status={m.status} />}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <InfoGrid cols={2}>
            <InfoRow label="Asset Tag" value={m.asset_tag} />
            <InfoRow label="Serial No." value={m.serial_number || "—"} />
            <InfoRow label="Category" value={m.category || "—"} />
            <InfoRow label="Location" value={m.location || "—"} />
            <InfoRow label="Install Date" value={fmtDate(m.install_date)} />
            <InfoRow label="Last Service" value={fmtDate(m.last_service_date)} />
            <InfoRow label="Next Service" value={fmtDate(m.next_service_date)} />
            <InfoRow label="Status" value={m.status} />
          </InfoGrid>
          {m.notes && (
            <div className="mt-4 phizo-card p-4">
              <div className="phizo-label mb-2">Notes</div>
              <p className="text-sm whitespace-pre-wrap">{m.notes}</p>
            </div>
          )}
        </div>

        {m.photo_url && (
          <div className="phizo-card p-3">
            <div className="phizo-label mb-2">Photo</div>
            <img src={m.photo_url} alt={m.name} className="w-full object-cover" />
          </div>
        )}
      </div>

      <div className="mt-6 phizo-card">
        <div className="p-4 border-b border-[#e4e4e7]">
          <div className="phizo-label">Maintenance History</div>
        </div>
        {logs.length === 0 ? (
          <div className="p-5"><Empty label="No maintenance recorded yet." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr><th>Performed</th><th>Type</th><th>By</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id || l.performed_at}>
                    <td className="text-sm">{fmtDate(l.performed_at)}</td>
                    <td>{l.type || "service"}</td>
                    <td>{l.by_name || "—"}</td>
                    <td className="text-sm text-[#52525b]">{l.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DetailShell>
  );
}
